import { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Select, InputNumber, message, Statistic, Row, Col, Tabs, Progress, Alert, Tooltip, Popconfirm, Descriptions, Input } from 'antd';
import { ApiOutlined, ReloadOutlined, SwapOutlined, ExperimentOutlined, ThunderboltOutlined, SafetyCertificateOutlined, ArrowRightOutlined, SettingOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';
import { t } from '../utils/i18n';

// 固定类型映射
const TYPE_MAPPING = {
  text: 'Text / 对话',
  image: 'Image / 图像',
  embeddings: 'Embeddings / 嵌入',
  audio: 'Audio / 语音',
  video: 'Video / 视频',
  rerank: 'Rerank / 重排序',
  speech: 'Speech / 语音合成',
  transcription: 'Transcription / 语音识别',
};
const FIXED_TYPES = Object.keys(TYPE_MAPPING);

const RouteRouter = () => {
  const [loading, setLoading] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [allModels, setAllModels] = useState([]);  // 所有模型
  const [models, setModels] = useState([]);        // 当前筛选的模型
  const [fallbackConfig, setFallbackConfig] = useState(null);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [retryModalVisible, setRetryModalVisible] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [form] = Form.useForm();
  const [retryForm] = Form.useForm();
  const [modelType, setModelType] = useState('text');
  const [selectedFallbackModels, setSelectedFallbackModels] = useState([]);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [defaultStrategy, setDefaultStrategy] = useState('lowest_price'); // 默认策略
  const [defaultStrategyName, setDefaultStrategyName] = useState('最低价'); // 默认策略名称

  useEffect(() => {
    fetchData();
  }, []);

  // 当 modelType 切换时，更新当前类型的备用模型和筛选模型
  useEffect(() => {
    if (fallbackConfig) {
      const currentFallback = fallbackConfig[modelType] || [];
      setSelectedFallbackModels(currentFallback);
    }
    // 按类型筛选模型
    const filtered = allModels.filter(m => m.type === modelType && m.status === 'active');
    setModels(filtered);
  }, [modelType, fallbackConfig, allModels]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [strategiesRes, metricsRes, modelsRes, fallbackRes, defaultStrategyRes] = await Promise.all([
        adminAPI.getRouteStrategies(),
        adminAPI.getRouteMetrics(),
        adminAPI.getModelConfigs(),
        adminAPI.getFallbackModels(modelType),
        adminAPI.getDefaultStrategy(),
      ]);

      // 转换 strategies 字段名: key->value, name->label
      if (strategiesRes?.data) {
        const transformedStrategies = strategiesRes.data.map(s => ({
          value: s.key,
          label: s.name,
          description: s.description,
        }));
        setStrategies(transformedStrategies);
      }
      if (metricsRes?.data) setMetrics(metricsRes.data);
      if (modelsRes?.code === 0) {
        const allModelsList = modelsRes.data?.list || [];
        setAllModels(allModelsList);
        const filtered = allModelsList.filter(m => m.type === modelType && m.status === 'active');
        setModels(filtered);
      }
      if (fallbackRes?.data) {
        setFallbackConfig(fallbackRes.data);
        const currentFallback = fallbackRes.data[modelType] || [];
        setSelectedFallbackModels(currentFallback);
      }
      if (defaultStrategyRes?.data) {
        setDefaultStrategy(defaultStrategyRes.data.strategy || 'lowest_price');
        setDefaultStrategyName(defaultStrategyRes.data.strategy_name || '最低价');
      }
    } catch (error) {
      console.error('Failed to fetch router data:', error);
      message.error(t('fetch_data_failed') || '获取路由数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshModels = async () => {
    try {
      setRefreshLoading(true);
      await adminAPI.refreshGatewayModels();
      message.success('模型刷新成功');
      fetchData();
    } catch (error) {
      console.error('Refresh models failed:', error);
      message.error('模型刷新失败');
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleUpdateDefaultStrategy = async (strategy) => {
    try {
      console.log('Setting strategy to:', strategy);
      const res = await adminAPI.setDefaultStrategy(strategy);
      console.log('Strategy update response:', res);
      if (res?.data?.strategy) {
        setDefaultStrategy(res.data.strategy);
        setDefaultStrategyName(res.data.strategy_name || strategy);
        message.success('默认策略更新成功');
      } else {
        message.success('默认策略更新成功');
      }
    } catch (error) {
      console.error('Update default strategy failed:', error);
      console.error('Error response:', error.response?.data);
      message.error('默认策略更新失败: ' + (error.message || error.response?.data?.message || ''));
    }
  };

  const handleTestRoute = async () => {
    try {
      const values = await form.validateFields();
      setTestLoading(true);
      setTestResult(null);

      const res = await adminAPI.testRoute({
        model_type: values.model_type,
        strategy: values.strategy,
        max_cost: values.max_cost ? Number(values.max_cost) : 0,
      }).catch(() => null);

      if (res?.data) {
        setTestResult(res.data);
        message.success('路由测试成功');
      } else if (res) {
        setTestResult(res);
        message.success('路由测试成功');
      } else {
        message.error('路由测试失败');
      }
    } catch (error) {
      console.error('Test route failed:', error);
      message.error('路由测试失败: ' + (error.message || ''));
    } finally {
      setTestLoading(false);
    }
  };

  const handleUpdateRetryConfig = async () => {
    try {
      const values = await retryForm.validateFields();
      await adminAPI.updateRetryConfig(values);
      message.success('重试配置更新成功');
      setRetryModalVisible(false);
      retryForm.resetFields();
    } catch (error) {
      console.error('Update retry config failed:', error);
      message.error('重试配置更新失败');
    }
  };

  const handleUpdateFallback = async () => {
    try {
      await adminAPI.updateFallbackModels({
        model_type: modelType,
        models: selectedFallbackModels || [],
      });
      message.success('备用模型配置更新成功');
      fetchData();
    } catch (error) {
      console.error('Update fallback failed:', error);
      message.error('备用模型配置更新失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 策略表格列
  const strategyColumns = [
    {
      title: t('strategy_name'),
      dataIndex: 'label',
      key: 'label',
      render: (text, record) => (
        <Space>
          <Tag color={getStrategyColor(record.value)}>{text}</Tag>
          {record.value === 'lowest_price' && <Tag color="green">推荐</Tag>}
        </Space>
      ),
    },
    {
      title: t('strategy_key'),
      dataIndex: 'value',
      key: 'value',
      render: (text) => <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4 }}>{text}</code>,
    },
    {
      title: t('strategy_description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('actions'),
      key: 'action',
      render: (_, record) => (
        <Button size="small" type="link" onClick={() => {
          form.setFieldsValue({ strategy: record.value, model_type: 'chat' });
          setTestModalVisible(true);
        }}>
          {t('test_route')}
        </Button>
      ),
    },
  ];

  // 获取类型显示名称
  const getTypeNameCn = (type) => {
    const names = {
      text: '对话', image: '图像', embeddings: '嵌入', audio: '语音', video: '视频',
      rerank: '重排序', speech: '语音合成', transcription: '语音识别', chat: '对话'
    };
    return names[type] || type || '-';
  };

  // 获取类型颜色
  const getTypeColor = (type) => {
    const colors = { text: 'blue', image: 'green', embeddings: 'purple', audio: 'orange', video: 'red', rerank: 'cyan', speech: 'geekblue', transcription: 'gold', chat: 'blue', embedding: 'purple' };
    return colors[type] || 'default';
  };

  // 获取计费方式名称
  const getBillingName = (type) => {
    const names = { per_token: 'Token', per_call: '按次', per_char: '字符', per_second: '按秒', free: '免费' };
    return names[type] || type;
  };

  // 模型表格列
  const modelColumns = [
    {
      title: t('model_name'),
      dataIndex: 'model_name',
      key: 'model_name',
      width: 180,
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 150,
    },
    {
      title: t('provider'),
      dataIndex: ['provider', 'display_name'],
      key: 'provider',
      width: 100,
    },
    {
      title: '计费方式',
      dataIndex: 'billing_type',
      key: 'billing_type',
      width: 90,
      render: (t) => <Tag>{getBillingName(t)}</Tag>,
    },
    {
      title: '价格',
      dataIndex: 'input_price',
      key: 'input_price',
      width: 200,
      render: (_, record) => {
        const { billing_type, input_price, output_price, billing_rate } = record;
        switch (billing_type) {
          case 'per_token':
            return <span>¥{parseFloat(input_price || 0).toFixed(6)} / ¥{parseFloat(output_price || 0).toFixed(6)} / 1K</span>;
          case 'per_call':
            return <span>¥{parseFloat(billing_rate || 0).toFixed(4)} / 次</span>;
          case 'per_char':
            return <span>¥{parseFloat(billing_rate || 0).toFixed(6)} / 字符</span>;
          case 'per_second':
            return <span>¥{parseFloat(billing_rate || 0).toFixed(6)} / 秒</span>;
          case 'free':
            return <Tag color="green">免费</Tag>;
          default:
            return `¥${parseFloat(input_price || 0).toFixed(6)}`;
        }
      },
    },
    {
      title: '热门',
      dataIndex: 'is_hot',
      key: 'is_hot',
      width: 70,
      render: (v) => v ? <Tag color="red">是</Tag> : <span>-</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (s) => (
        <Tag color={s === 'active' ? 'green' : 'red'}>
          {s === 'active' ? '启用' : '禁用'}
        </Tag>
      ),
    },
  ];

  const getStrategyColor = (strategy) => {
    const colors = {
      lowest_price: 'green',
      performance: 'blue',
      balanced: 'purple',
      fallback: 'orange',
      direct: 'red',
    };
    return colors[strategy] || 'default';
  };

  return (
    <div className="route-router-page">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('total_requests')}
              value={metrics?.total_requests || 0}
              prefix={<ApiOutlined />}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('success_rate')}
              value={metrics?.success_rate?.toFixed(1) || 0}
              suffix="%"
              prefix={<SafetyCertificateOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('avg_latency')}
              value={metrics?.avg_latency?.toFixed(0) || 0}
              suffix="ms"
              prefix={< ThunderboltOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title={t('failed_requests')}
              value={metrics?.failed_requests || 0}
              prefix={<SwapOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'settings',
            label: '全局设置',
            children: (
              <Card
                title="全局路由设置"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={fetchData}>
                    {t('refresh')}
                  </Button>
                }
              >
                <Alert
                  message="全局设置：选择平台默认使用的路由策略，用户发起请求时未指定策略将使用此处配置的策略"
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                  {strategies.map((s) => (
                    <Card
                      key={`strategy-card-${s.value}`}
                      size="small"
                      hoverable
                      onClick={() => handleUpdateDefaultStrategy(s.value)}
                      style={{
                        width: 160,
                        textAlign: 'center',
                        cursor: 'pointer',
                        border: defaultStrategy === s.value ? '2px solid #1677ff' : '1px solid #d9d9d9',
                        background: defaultStrategy === s.value ? '#e6f4ff' : '#fff',
                      }}
                    >
                      <Tag color={getStrategyColor(s.value)} style={{ marginBottom: 8 }}>{s.label}</Tag>
                      {defaultStrategy === s.value && (
                        <Tag color="blue" style={{ position: 'absolute', top: 8, right: 8 }}>当前</Tag>
                      )}
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{s.description}</div>
                    </Card>
                  ))}
                </div>
                <div style={{ marginTop: 24, padding: 16, background: '#f6ffed', borderRadius: 8, border: '1px solid #b7eb8f' }}>
                  <Tag color="blue" style={{ fontSize: 14 }}>{defaultStrategyName}</Tag>
                  <span style={{ marginLeft: 12, color: '#52c41a' }}>✓ 当前生效</span>
                </div>
              </Card>
            ),
          },
          {
            key: 'strategies',
            label: t('route_strategies'),
            children: (
              <Card
                title={t('route_strategies')}
                extra={
                  <Button icon={<ReloadOutlined />} onClick={fetchData}>
                    {t('refresh')}
                  </Button>
                }
              >
                <Alert
                  message={t('strategy_tip')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Table
                  columns={strategyColumns}
                  dataSource={strategies}
                  rowKey={(record) => `strategy-${record.value}`}
                  pagination={false}
                  loading={loading}
                />
              </Card>
            ),
          },
          {
            key: 'models',
            label: t('available_models'),
            children: (
              <Card
                title={t('available_models')}
                extra={
                  <Space>
                    <Select
                      value={modelType}
                      onChange={setModelType}
                      style={{ width: 200 }}
                    >
                      {FIXED_TYPES.map(type => {
                        const count = allModels.filter(m => m.type === type && m.status === 'active').length;
                        const displayName = TYPE_MAPPING[type] || type;
                        return <Select.Option key={type} value={type}>{displayName} ({count})</Select.Option>;
                      })}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={fetchData}>
                      {t('refresh')}
                    </Button>
                    <Button icon={<ApiOutlined />} onClick={handleRefreshModels} loading={refreshLoading}>
                      刷新缓存
                    </Button>
                  </Space>
                }
              >
                <Alert
                  message={`当前类型: ${getTypeNameCn(modelType)}，共 ${models.length} 个可用模型`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Table
                  columns={modelColumns}
                  dataSource={models}
                  rowKey={(record) => `model-${record.model_name}`}
                  pagination={{ pageSize: 10 }}
                  loading={loading}
                  scroll={{ x: 1000 }}
                />
              </Card>
            ),
          },
          {
            key: 'modelStats',
            label: '模型统计',
            children: (
              <Card title="各类型模型统计">
                <Row gutter={[16, 16]}>
                  {FIXED_TYPES.map(type => {
                    const typeModels = allModels.filter(m => m.type === type);
                    const activeModels = typeModels.filter(m => m.status === 'active');
                    return (
                      <Col xs={24} sm={12} md={6} key={type}>
                        <Card size="small">
                          <Statistic
                            title={<><Tag color={getTypeColor(type)}>{getTypeNameCn(type)}</Tag></>}
                            value={activeModels.length}
                            suffix={<span style={{ fontSize: 12 }}>/ {typeModels.length}</span>}
                            valueStyle={{ color: activeModels.length > 0 ? '#1677ff' : '#999' }}
                          />
                          <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                            启用/总数
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            ),
          },
          {
            key: 'fallback',
            label: t('fallback_config'),
            children: (
              <Card
                title={t('fallback_config')}
                extra={
                  <Button type="primary" icon={<SafetyCertificateOutlined />} onClick={() => setRetryModalVisible(true)}>
                    {t('retry_config')}
                  </Button>
                }
              >
                <Descriptions variant="bordered" column={2}>
                  <Descriptions.Item label={t('model_type')}>
                    <Select value={modelType} onChange={setModelType} style={{ width: 200 }}>
                      {FIXED_TYPES.map(type => (
                        <Select.Option key={type} value={type}>{TYPE_MAPPING[type] || type}</Select.Option>
                      ))}
                    </Select>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('fallback_models')}>
                    <Select
                      mode="tags"
                      value={selectedFallbackModels}
                      placeholder={t('select_fallback_models')}
                      style={{ width: 300 }}
                      onChange={setSelectedFallbackModels}
                    >
                      {models.map((m) => (
                        <Select.Option key={`model-opt-${m.model_name}`} value={m.model_name}>
                          {m.display_name || m.model_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 16 }}>
                  <Button type="primary" onClick={handleUpdateFallback}>
                    {t('save_config')}
                  </Button>
                </div>
                <div style={{ marginTop: 24 }}>
                  <h4>{t('current_fallback')}</h4>
                  <Space wrap>
                    {(fallbackConfig?.[modelType] || []).map((model, idx) => (
                      <Tag key={model || `fallback-${idx}`} color="blue" icon={<ArrowRightOutlined />}>
                        {idx + 1}. {model}
                      </Tag>
                    ))}
                  </Space>
                </div>
              </Card>
            ),
          },
        ]}
      />

      {/* 路由测试弹窗 */}
      <Modal
        title={t('route_test')}
        open={testModalVisible}
        onCancel={() => {
          setTestModalVisible(false);
          setTestResult(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="model_type" label={t('model_type')} rules={[{ required: true }]}>
            <Select style={{ width: '100%' }}>
              {FIXED_TYPES.map(type => (
                <Select.Option key={type} value={type}>{TYPE_MAPPING[type] || type}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="strategy" label={t('strategy')} rules={[{ required: true }]}>
            <Select style={{ width: '100%' }}>
              {strategies.map((s) => (
                <Select.Option key={`strategy-opt-${s.value}`} value={s.value}>{s.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="max_cost" label={t('max_cost_per_request')}>
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber min={0} step={0.001} style={{ width: '100%' }} />
              <Input style={{ width: 60, textAlign: 'center' }} defaultValue="¥" disabled />
            </Space.Compact>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" loading={testLoading} onClick={handleTestRoute}>
                {t('start_test')}
              </Button>
              <Button onClick={() => {
                setTestModalVisible(false);
                setTestResult(null);
              }}>
                {t('close')}
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {testResult && (
          <div style={{ marginTop: 16 }}>
            <Alert message={t('test_result')} type="success" showIcon />
            <Descriptions variant="bordered" column={2} style={{ marginTop: 16 }}>
              <Descriptions.Item label={t('selected_model')}>{testResult.display_name}</Descriptions.Item>
              <Descriptions.Item label={t('provider')}>{testResult.provider}</Descriptions.Item>
              <Descriptions.Item label={t('input_price')}>{testResult.input_price ? `¥${testResult.input_price}/1K` : '-'}</Descriptions.Item>
              <Descriptions.Item label={t('output_price')}>{testResult.output_price ? `¥${testResult.output_price}/1K` : '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 重试配置弹窗 */}
      <Modal
        title={t('retry_config')}
        open={retryModalVisible}
        onCancel={() => {
          setRetryModalVisible(false);
          retryForm.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => setRetryModalVisible(false)}>
            {t('cancel')}
          </Button>,
          <Button key="save" type="primary" onClick={handleUpdateRetryConfig}>
            {t('save_config')}
          </Button>,
        ]}
      >
        <Form form={retryForm} layout="vertical">
          <Form.Item name="max_retries" label={t('max_retries')}>
            <InputNumber min={0} max={10} defaultValue={3} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="initial_delay" label={t('initial_delay')}>
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber min={100} step={100} defaultValue={1000} style={{ width: '100%' }} />
              <Input style={{ width: 60, textAlign: 'center' }} defaultValue="ms" disabled />
            </Space.Compact>
          </Form.Item>
          <Form.Item name="max_delay" label={t('max_delay')}>
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber min={1000} step={1000} defaultValue={10000} style={{ width: '100%' }} />
              <Input style={{ width: 60, textAlign: 'center' }} defaultValue="ms" disabled />
            </Space.Compact>
          </Form.Item>
          <Form.Item name="backoff_factor" label={t('backoff_factor')}>
            <InputNumber min={1} max={5} step={0.1} defaultValue={2} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RouteRouter;
